import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Edit2, Trash2, Target, Trophy, CheckCircle2 } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Skeleton } from '../../../components/ui/Skeleton'
import { RowActionsMenu } from '../../../components/ui/RowActionsMenu'
import { EmptyState } from '../../../components/EmptyState'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'
import { findThresholdMonth, findAllInstallmentsClearedMonth, findAllObligationsClearedMonth } from '../../../lib/cashflowForecast'
import { cn } from '../../../lib/utils'

const TYPES = [
  'balance_threshold',
  'obligation_complete',
  'all_installments_complete',
  'all_obligations_complete',
  'ongoing_rule',
  'custom',
]

function fmt(n) {
  if (n == null || isNaN(n)) return '—'
  return Math.round(Number(n)).toLocaleString('en-US')
}

function TargetForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name || '')
  const [type, setType] = useState(initial?.type || 'balance_threshold')
  const [targetValue, setTargetValue] = useState(initial?.target_value?.toString() || '')
  const [targetDate, setTargetDate] = useState(initial?.target_date || '')
  const [notes, setNotes] = useState(initial?.notes || '')

  const needsValue = type === 'balance_threshold'

  const submit = (e) => {
    e.preventDefault()
    onSave({
      name: name.trim(),
      type,
      target_value: needsValue && targetValue ? Number(targetValue) : null,
      target_date: targetDate || null,
      notes: notes.trim() || null,
      status: initial?.status || 'in_progress',
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <input type="text" required maxLength={80}
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder={t('finance.targets.name')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      <select value={type} onChange={(e) => setType(e.target.value)} dir="auto"
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm">
        {TYPES.map((tp) => (
          <option key={tp} value={tp}>{t(`finance.targets.types.${tp}`)}</option>
        ))}
      </select>
      {needsValue && (
        <input type="number" step="0.01" dir="ltr"
          value={targetValue} onChange={(e) => setTargetValue(e.target.value)}
          placeholder={t('finance.targets.targetValue')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
      )}
      <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} dir="ltr"
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
      <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={120}
        placeholder={t('finance.targets.notesPlaceholder')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting || !name.trim()} className="flex-1">{t('common.save')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  )
}

function TargetCard({ target, projection, onEdit, onDelete, onAchieve }) {
  const { t } = useTranslation()
  const achieved = target.status === 'achieved'
  const missed = target.status === 'missed'

  const progress = (() => {
    if (target.type === 'balance_threshold' && target.target_value > 0) {
      const current = projection?.currentBalance ?? 0
      const pct = Math.max(0, Math.min(100, (current / target.target_value) * 100))
      return { pct, currentLabel: fmt(current), totalLabel: fmt(target.target_value) }
    }
    return null
  })()

  const expectedMonth = projection?.expectedByTarget?.[target.id] || null

  return (
    <Card className={cn(achieved && 'opacity-70')}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          {achieved ? (
            <Trophy className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          ) : (
            <Target className="w-5 h-5 text-app-primary-500 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={cn('text-sm font-semibold', achieved && 'line-through')} dir="auto">
                {target.name}
              </p>
              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" dir="auto">
                {t(`finance.targets.types.${target.type}`)}
              </span>
              {missed && (
                <span className="text-[10px] text-red-600 bg-red-500/10 ring-1 ring-red-500/30 px-1.5 py-0.5 rounded" dir="auto">
                  {t('finance.targets.missed')}
                </span>
              )}
            </div>

            {progress && (
              <div className="mt-2">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-app-primary-500 transition-all" style={{ width: `${progress.pct}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 font-mono" dir="ltr">
                  {progress.currentLabel} / {progress.totalLabel} SAR
                </p>
              </div>
            )}

            {target.target_date && (
              <p className="text-[11px] text-muted-foreground mt-1" dir="auto">
                {t('finance.targets.targetDate')}: <span className="font-mono" dir="ltr">{target.target_date}</span>
              </p>
            )}
            {expectedMonth && !achieved && (
              <p className="text-[11px] text-emerald-600 mt-0.5" dir="auto">
                {t('finance.targets.expectedBy')}: <span className="font-mono" dir="ltr">{expectedMonth}</span>
              </p>
            )}
            {target.achieved_at && (
              <p className="text-[11px] text-emerald-600 mt-0.5" dir="auto">
                {t('finance.targets.achievedOn')}: <span className="font-mono" dir="ltr">{target.achieved_at}</span>
              </p>
            )}
            {target.notes && (
              <p className="text-[11px] text-muted-foreground mt-1" dir="auto">{target.notes}</p>
            )}
          </div>
          <RowActionsMenu
            ariaLabel={t('common.actions')}
            actions={[
              ...(!achieved && onAchieve ? [{
                icon: CheckCircle2,
                label: t('finance.targets.markAchieved'),
                onClick: onAchieve,
              }] : []),
              { icon: Edit2,  label: t('common.edit'),   onClick: onEdit },
              { icon: Trash2, label: t('common.delete'), onClick: onDelete, variant: 'destructive' },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function FinancialTargetsSection({ data, currentBalance, forecastRows, installments, obligations }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const confirm = useConfirm()

  // Pre-compute the "expected completion month" for each target type
  // so the cards don't each re-walk the forecast rows.
  const projection = useMemo(() => {
    const installmentsCleared = findAllInstallmentsClearedMonth(installments)
    const obligationsCleared = findAllObligationsClearedMonth(obligations)
    const expectedByTarget = {}
    for (const tgt of data.targets) {
      if (tgt.type === 'balance_threshold' && tgt.target_value > 0) {
        expectedByTarget[tgt.id] = findThresholdMonth(forecastRows, Number(tgt.target_value))
      } else if (tgt.type === 'all_installments_complete') {
        expectedByTarget[tgt.id] = installmentsCleared
      } else if (tgt.type === 'all_obligations_complete') {
        expectedByTarget[tgt.id] = obligationsCleared
      }
    }
    return { currentBalance, expectedByTarget }
  }, [currentBalance, data.targets, forecastRows, installments, obligations])

  const active = data.targets.filter((t) => t.status !== 'achieved')
  const achieved = data.targets.filter((t) => t.status === 'achieved')

  const handleSave = async (values) => {
    setSubmitting(true)
    if (editing) await data.update(editing.id, values)
    else await data.create(values)
    setSubmitting(false)
    setAdding(false)
    setEditing(null)
  }

  const handleAchieve = async (target) => {
    const ok = await confirm({
      title: t('finance.targets.confirmAchieve'),
      description: target.name,
      confirmLabel: t('finance.targets.markAchieved'),
      variant: 'default',
      icon: Trophy,
    })
    if (ok) {
      await data.update(target.id, {
        status: 'achieved',
        achieved_at: new Date().toLocaleDateString('en-CA'),
      })
    }
  }

  const handleDelete = async (target) => {
    const ok = await confirm({
      title: t('finance.targets.confirmDelete'),
      description: target.name,
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await data.remove(target.id)
  }

  return (
    <>
      <section className="space-y-2 pt-2">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
            <Target className="w-4 h-4 text-app-primary-500" />
            {t('finance.targets.title')}
          </h2>
          {!adding && !editing && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4 me-1" />
              {t('finance.targets.add')}
            </Button>
          )}
        </div>

        {adding && (
          <TargetForm onSave={handleSave} onCancel={() => setAdding(false)} submitting={submitting} />
        )}

        {data.loading ? (
          <Skeleton className="h-20 w-full" />
        ) : active.length === 0 && !adding ? (
          <EmptyState icon={Target} title={t('finance.targets.empty')} />
        ) : (
          active.map((tgt) => (
            <React.Fragment key={tgt.id}>
              {editing?.id === tgt.id ? (
                <TargetForm initial={tgt} onSave={handleSave} onCancel={() => setEditing(null)} submitting={submitting} />
              ) : (
                <TargetCard
                  target={tgt}
                  projection={projection}
                  onEdit={() => setEditing(tgt)}
                  onDelete={() => handleDelete(tgt)}
                  onAchieve={() => handleAchieve(tgt)}
                />
              )}
            </React.Fragment>
          ))
        )}
      </section>

      {achieved.length > 0 && (
        <section className="space-y-2 pt-2">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5 px-1" dir="auto">
            <Trophy className="w-4 h-4 text-amber-500" />
            {t('finance.targets.achievementsTitle')}
          </h2>
          {achieved.map((tgt) => (
            <TargetCard
              key={tgt.id}
              target={tgt}
              projection={projection}
              onEdit={() => setEditing(tgt)}
              onDelete={() => handleDelete(tgt)}
            />
          ))}
        </section>
      )}
    </>
  )
}
